import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Bot } from "lucide-react";
import { AgentPhaseBadge, phaseVariant } from "@/lib/lifecycleBadge";
import { cn } from "@/lib/utils";
import type { Agent } from "@/types";

export interface AgentNodeData extends Record<string, unknown> {
  agent: Agent;
  dimmed?: boolean;
}

// Phase → linker Akzent-Balken (gleiche Logik wie Phase-Badge).
function accentBorder(phase: string): string {
  const v = phaseVariant(phase);
  if (v === "online") return "border-l-ok";
  if (v === "enrolled") return "border-l-info";
  if (v === "revoked") return "border-l-danger";
  if (v === "busy") return "border-l-warn";
  return "border-l-border-strong";
}

export function AgentNode(props: NodeProps) {
  const { agent, dimmed } = props.data as AgentNodeData;
  const handleCls = "!h-2.5 !w-2.5 !border-2 !border-surface !bg-accent";
  return (
    <div
      className={cn(
        "w-[208px] rounded-lg border border-l-[3px] border-border bg-surface px-3 py-2.5 shadow-sm transition-opacity",
        accentBorder(agent.lifecyclePhase),
        props.selected && "ring-2 ring-accent",
        dimmed && "opacity-25",
      )}
    >
      <Handle type="target" position={Position.Left} className={handleCls} isConnectable={!dimmed} />
      <div className="flex items-center gap-2.5">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border bg-surface2 text-text3">
          <Bot className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold text-text">{agent.name}</div>
          <div className="mono truncate text-[10.5px] text-text3">{agent.id}</div>
        </div>
      </div>
      <div className="mt-2">
        {agent.revokedAt ? <AgentPhaseBadge phase="unknown" /> : <AgentPhaseBadge phase={agent.lifecyclePhase} />}
      </div>
      <Handle type="source" position={Position.Right} className={handleCls} isConnectable={!dimmed} />
    </div>
  );
}
